import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { BookOpen, Home, GraduationCap, Bookmark, PlayCircle } from "lucide-react";

interface CourseRow { id: string; title: string }
interface LessonRow { id: string; title: string; course_id: string }

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open || courses.length > 0) return;
    (async () => {
      const [c, l] = await Promise.all([
        supabase.from("courses").select("id, title").eq("is_published", true),
        supabase.from("lessons").select("id, title, course_id"),
      ]);
      setCourses((c.data as CourseRow[]) || []);
      setLessons((l.data as LessonRow[]) || []);
    })();
  }, [open, courses.length]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search courses, lessons or pages…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => go("/dashboard")}>
            <Home className="mr-2 w-4 h-4" /> Dashboard
          </CommandItem>
          <CommandItem onSelect={() => go("/dashboard/bookmarks")}>
            <Bookmark className="mr-2 w-4 h-4" /> Bookmarks
          </CommandItem>
          <CommandItem onSelect={() => go("/")}>
            <Home className="mr-2 w-4 h-4" /> Home
          </CommandItem>
        </CommandGroup>
        {courses.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Courses">
              {courses.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/dashboard?course=${c.id}`)}>
                  <BookOpen className="mr-2 w-4 h-4" /> {c.title}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {lessons.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Lessons">
              {lessons.slice(0, 30).map((l) => (
                <CommandItem
                  key={l.id}
                  onSelect={() => go(`/dashboard?course=${l.course_id}&lesson=${l.id}`)}
                >
                  <PlayCircle className="mr-2 w-4 h-4" /> {l.title}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
